import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { I18nContext } from "../../store/context/i18nContext";
import "./style.css";
import InputFieldWrapper from "./InputFieldWrapper";
import { useTranslation } from "../../store/context/TranslationContext";
import PreviousValue from "./PreviousValue";
import { INPUT_AUTOFILL_PROPS, mergeAutofillProps } from "./autofillProps";
import { buildAutomationChildTestId, resolveAutomationTestId } from "./testId";

const INPUT_COMPONENT_ONLY_PROPS = new Set([
  "capital",
  "checker",
  "className",
  "desc",
  "error",
  "hideBorder",
  "identifier",
  "innerContainerClassName",
  "inline",
  "inputFieldRef",
  "inputFieldStyle",
  "isDisabled",
  "isFocused",
  "isReadOnly",
  "label",
  "labelStyle",
  "LeftComponent",
  "noLabel",
  "onClickLeft",
  "onClickRight",
  "onblur",
  "outline",
  "outerClassName",
  "plain",
  "prevValue",
  "removeBottomMargin",
  "required",
  "RightComponent",
  "setIsFocused",
  "setShowClearState",
  "showClear",
  "showClearState",
  "showErr",
  "style",
  "suggestion",
]);

const NATIVE_INPUT_PROP_ALLOWLIST = new Set([
  "accept",
  "alt",
  "aria-activedescendant",
  "aria-autocomplete",
  "aria-controls",
  "aria-describedby",
  "aria-expanded",
  "aria-invalid",
  "aria-label",
  "aria-labelledby",
  "aria-owns",
  "autoCapitalize",
  "autoComplete",
  "autoCorrect",
  "autoFocus",
  "capture",
  "checked",
  "defaultChecked",
  "defaultValue",
  "disabled",
  "enterKeyHint",
  "form",
  "id",
  "inputMode",
  "list",
  "mask",
  "maskChar",
  "maskPlaceholder",
  "alwaysShowMask",
  "beforeMaskedStateChange",
  "formatChars",
  "max",
  "maxLength",
  "min",
  "minLength",
  "multiple",
  "name",
  "pattern",
  "placeholder",
  "readOnly",
  "required",
  "role",
  "spellCheck",
  "step",
  "tabIndex",
  "title",
  "type",
  "value",
]);

const shouldPassThroughInputProp = (propName) => {
  if (!propName || INPUT_COMPONENT_ONLY_PROPS.has(propName)) {
    return false;
  }

  if (propName.startsWith("data-") || propName.startsWith("aria-")) {
    return true;
  }

  if (/^on[A-Z]/.test(propName)) {
    return true;
  }

  return NATIVE_INPUT_PROP_ALLOWLIST.has(propName);
};

const getNativeInputProps = (props = {}) => Object.entries(props).reduce((accumulator, [key, value]) => {
  if (shouldPassThroughInputProp(key)) {
    accumulator[key] = value;
  }
  return accumulator;
}, {});

const assignRef = (ref, value) => {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  ref.current = value;
};

export const InputField = React.forwardRef((props, forwardedRef) => {
  const {
    noLabel,
    suggestion,
    max,
    onKeyDown,
    removeBottomMargin,
    className,
    error,
    desc,
    showErr,
    style,
    plain,
    inline,
    innerContainerClassName,
    onClickRight,
    onClickLeft,
    LeftComponent,
    RightComponent,
    outline,
    label = "",
    required,
    isReadOnly,
    isDisabled,
    hideBorder = false,
    outerClassName,
    autoFocus = false,
    onClick = () => { },
    onblur,
    labelStyle,
    inputFieldStyle,
  } = props;

  const [showClearState, setShowClearState] = useState(false);
  const inputFieldRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const { t, } = useTranslation();
  const fieldTestId = resolveAutomationTestId(props["data-testid"], props.id, props.name, props.identifier);
  const nativeInputProps = props?.type === "file"
    ? getNativeInputProps(props)
    : mergeAutofillProps(getNativeInputProps(props), INPUT_AUTOFILL_PROPS);

  return (
    <section
      onClick={onClick}
      data-testid={fieldTestId}
      style={{ ...style }}
      className={` inputfield__container  ${className} ${!(error && showErr) ? removeBottomMargin : " " ? "mb-3" : ""
        }`}
    >
      {suggestion && <b className="inputfield__suggestion" data-testid={buildAutomationChildTestId(fieldTestId, "suggestion")}>{suggestion}</b>}

      {!(inline || plain || outline) && label &&
        (!noLabel ? (
          <div
            data-testid={buildAutomationChildTestId(fieldTestId, "label")}
            style={{
              minWidth: "120px",
              fontSize: 16,
              margin: "0px 0px 5px 5px",
              fontWeight: "500",
              textAlign: "left",
              ...labelStyle,
            }}
          >
            {t(label)}
            {label && required && <font color="red">*</font>}
          </div>
        ) : null)}

      <div
        ref={inputFieldRef}
        className={
          "flex-grow-1 " +
          (plain && !isFocused ? "inputfield__plain " : "") +
          (outline
            ? ` inputfield__innercontainer ${!!props?.prevValue?.has ? "is-changed" : isDisabled ? "is-disabled" : ""}`
            : inline
              ? " inputfield__innerInline "
              : plain
                ? " "
                : hideBorder
                  ? "inputfield__innercontainer_noborder"
                  : ` inputfield__innercontainer ${!!props?.prevValue?.has ? "is-changed" : isDisabled ? "is-disabled" : ""}`) +
          (innerContainerClassName ? innerContainerClassName : "")
        }
        style={
          inline ? { height: "21px", ...style } : inputFieldStyle ? inputFieldStyle : style
        }
      >
        {LeftComponent && (
          <div
            data-testid={buildAutomationChildTestId(fieldTestId, "left")}
            onClick={(e) => (onClickLeft ? onClickLeft(e) : null)}
            className={
              onClickLeft
                ? "component_pointer inputfield__leftComponent"
                : "inputfield__leftComponent"
            }
          >
            <props.LeftComponent />
          </div>
        )}

        <MainInputField
          max={max}
          isFocused={isFocused}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          onBlur={(v) => {
            if (onblur) onblur(v);
          }}
          autoFocus={autoFocus}
          setIsFocused={setIsFocused}
          {...props}
          nativeInputProps={nativeInputProps}
          forwardedRef={forwardedRef}
          setShowClearState={setShowClearState}
          showClearState={showClearState}
          required={required}
          outerClassName={outerClassName}
          labelStyle={labelStyle}
        />

        {RightComponent && (
          <div
            data-testid={buildAutomationChildTestId(fieldTestId, "right")}
            onClick={(e) => (onClickRight ? onClickRight(e) : null)}
            style={{ height: "32px" }}
            className={
              onClickRight
                ? "component_pointer inputfield__rightComponent"
                : "inputfield__rightComponent"
            }
          >
            <props.RightComponent {...props} />
          </div>
        )}
      </div>

      {desc ? <em className="inputfield__desc d-block" data-testid={buildAutomationChildTestId(fieldTestId, "description")}>
        {desc ? desc : ""}
      </em> : null}

      <PreviousValue
        value={props?.prevValue?.value}
        show={props?.prevValue?.has}
        testId={buildAutomationChildTestId(fieldTestId, "previous")}
      />

      {!isDisabled && error && showErr ? (
        <em className="inputfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>
          {error ? t(error) : "Please enter Something"}
        </em>
      ) : null}
    </section>
  );
});

InputField.displayName = "InputField";

const MainInputField = (props) => {
  const {
    id,
    placeholder,
    max,
    showClear,
    label,
    inline,
    onKeyDown = () => { },
    plain,
    outline,
    style,
    isDisabled,
    isReadOnly,
    maxLength,
    noLabel,
    pattern,
    name,
    identifier,
    onFocus,
    onBlur,
    checker,
    outerClassName,
    autoFocus,
    labelStyle,
    regex,
    capital,
    nativeInputProps = {},
    forwardedRef,
  } = props;

  const { t } = useContext(I18nContext);
  const { setShowClearState, isFocused, setIsFocused } = props;

  const [value, setValue] = useState(props?.value);
  const [width, setWidth] = useState(props?.value?.length);
  const [type, setType] = useState(props?.type);
  const inputRef = useRef(null);
  const skipParentSyncOnNextValueEffectRef = useRef(false);
  const latestValueRef = useRef(props?.value);
  const fieldTestId = resolveAutomationTestId(props["data-testid"], id, name, identifier);
  const setInputNodeRef = useCallback((node) => {
    inputRef.current = node;
    assignRef(forwardedRef, node);
  }, [forwardedRef]);

  useEffect(() => {
    setType(props?.type);
  }, [props?.type]);

  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (props?.onChange && props?.value !== value) {
      if (skipParentSyncOnNextValueEffectRef.current) {
        skipParentSyncOnNextValueEffectRef.current = false;
        return;
      }
      props?.onChange(value);
    }
    if (showClear) {
      setShowClearState(value ? true : false);
    }
  }, [value]);

  useEffect(() => {
    if (props?.value !== value) {
      setValue(props?.value);
    }
    if (showClear) {
      setShowClearState(props?.value ? true : false);
    }
  }, [props?.value]);

  const changeHandler = (v) => {
    setWidth(v);
  };

  const syncDomValueToState = useCallback((rawValue, {
    trim = false,
    notifyParentImmediately = false,
    flushParentImmediately = false,
  } = {}) => {
    let nextValue = rawValue;

    if (typeof nextValue === "string") {
      nextValue = trim ? nextValue.trim() : nextValue;
      nextValue = regex ? nextValue.split('').filter(char => regex.test(char)).join('') : nextValue;
      if (capital) {
        nextValue = nextValue.toUpperCase();
      }
    }

    setValue(nextValue);
    if (showClear) {
      setShowClearState(Boolean(nextValue));
    }

    if (notifyParentImmediately && props?.onChange && props?.value !== nextValue) {
      skipParentSyncOnNextValueEffectRef.current = true;
      if (flushParentImmediately) {
        flushSync(() => {
          props.onChange(nextValue);
        });
      } else {
        props.onChange(nextValue);
      }
    }

    return nextValue;
  }, [capital, props, regex, setShowClearState, showClear]);

  useEffect(() => {
    if (props?.type === "file" || typeof window === "undefined") {
      return undefined;
    }

    const syncAutofilledValue = () => {
      const node = inputRef.current;
      if (!node || document.activeElement === node) {
        return;
      }

      const domValue = node.value ?? "";
      const stateValue = latestValueRef.current == null ? "" : String(latestValueRef.current);

      if (domValue !== stateValue) {
        syncDomValueToState(domValue, {
          trim: true,
          notifyParentImmediately: true,
        });
      }
    };

    syncAutofilledValue();

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      syncAutofilledValue();
      attempts += 1;
      if (attempts >= 20) {
        window.clearInterval(intervalId);
      }
    }, 250);

    const handleWindowFocus = () => {
      window.setTimeout(syncAutofilledValue, 120);
    };

    window.addEventListener("pageshow", handleWindowFocus);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("pageshow", handleWindowFocus);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [props?.type, syncDomValueToState]);

  const Validate = (evt) => {
    const inputValue = capital ? evt.target.value.toUpperCase() : evt.target.value;
    const filteredValue = regex ? inputValue.split('').filter(char => regex.test(char)).join('') : inputValue;
    return filteredValue;
  }

  return (
    <div
      className={inline ? "input-group" : `inputfield ps-2 ${outerClassName}`}
      style={inline ? { height: "21px" } : {}}
    >
      {inline && label && (
        <div
          style={{ height: "20px", fontSize: 11, ...labelStyle }}
          className="input-group-text"
        >
          {t(label)}
        </div>
      )}
      <InputFieldWrapper
        {...nativeInputProps}
        data-testid={buildAutomationChildTestId(fieldTestId, "input")}
        ref={setInputNodeRef}
        onKeyDown={onKeyDown}
        disabled={isDisabled}
        readOnly={isReadOnly}
        autoFocus={autoFocus}
        maxLength={maxLength ? maxLength : "100"}
        pattern={pattern}
        id={id ?? `custom-input-${label}-${value}-${identifier}`}
        name={name}
        max={max}
        type={type}
        required={props?.required}
        onFocus={() => {
          if (onFocus) onFocus();
          setIsFocused(true);
        }}
        onBlur={(e) => {
          const nextValue = props?.type === "file"
            ? e.target.files
            : syncDomValueToState(e.target.value, {
              trim: true,
              notifyParentImmediately: true,
              flushParentImmediately: true,
            });
          if (onBlur) onBlur(nextValue);
          setIsFocused(false);
        }}
        onChange={(evt) => {
          if (props?.datePicker) {
            props?.onChange && props?.onChange(evt);
            return;
          }
          const filteredValue = Validate(evt);
          syncDomValueToState(
            props?.type === "file" ? evt.target.files : filteredValue
          );
          if (inline) changeHandler(filteredValue);
        }}
        style={
          inline
            ? {
              ...style,
              minWidth: "40px",
              height: "20px",
              paddingLeft: "3px",
              paddingRight: "3px",
              width:
                type === "text" || type === "email"
                  ? width + 0.5 + "ch"
                  : "auto",
              maxWidth:
                type === "text" || type === "email" ? 18 + "ch" : "auto",
            }
            : { ...style }
        }
        value={props?.type === "file" ? undefined : (value ?? "")}
        className={
          inline ? "form-control rounded-right-1" : `inputfield__input ${isDisabled ? "cursor-disabled" : ""}`
        }
        placeholder={`${placeholder && (isFocused || noLabel)
          ? t(placeholder)
          : !(outline || plain)
            ? placeholder
              ? t(placeholder)
              : ""
            : " "
          }`}
      />
      {props?.type === "password" ? (
        value ? (
          <span
            className="inputfield__eye"
            data-testid={buildAutomationChildTestId(fieldTestId, "toggle")}
            onClick={() =>
              type === "text" ? setType("password") : setType("text")
            }
          >
            {type === "text" ? (
              <i className="uil-eye text-primary" />
            ) : (
              <i className="uil-eye-slash text-primary" />
            )}
          </span>
        ) : undefined
      ) : undefined}
      {(plain || outline) && !noLabel && label ? <Label {...props} data-testid={buildAutomationChildTestId(fieldTestId, "label")} /> : null}
    </div>
  );
};

const Label = ({ label, required, identifier, value, labelStyle, "data-testid": testId }) => {
  const { t } = useTranslation();
  return (
    <label
      onClick={() => {
        document
          .getElementById(`custom-input-${label}-${value}-${identifier}`)
          ?.focus();
      }}
      style={{ ...labelStyle }}
      className="inputfield__label px-1"
      data-testid={testId}
    >
      {t(label)}
      {required && <font color="#EC734B">*</font>}
    </label>
  );
};
